/**
 * API client for BtGtR backend
 */

// Use the live Cloudflare Worker backend
const API_BASE_URL = 'https://btgtr-copilot-backend.better-to-give-than-receive.workers.dev/api/v1';

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface MagicLinkRequestResponse {
  success: boolean;
  message: string;
  isExistingUser: boolean;
  requiresBirthdate?: boolean;
}

export interface MagicLinkVerifyResponse {
  success: boolean;
  message?: string;
  sessionToken?: string;
  userId?: string;
  contact: string;
  contactType?: 'email' | 'phone';
  needsRegistration?: boolean;
  isEmail?: boolean;
  memberId?: string;
  giftGroupId?: string;
  redirectTo?: string;
  user?: {
    id: string;
    email?: string;
    phone?: string;
    name?: string;
    birthdate?: string;
    creditBalance: number;
  };
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  userId: string;
  sessionToken: string;
  contact: string;
  contactType: 'email' | 'phone';
}

export interface UserProfile {
  id: string;
  email?: string;
  phone?: string;
  name?: string;
  birthdate?: string;
  creditBalance: number;
  adminLevel?: number;
  createdAt: string;
  lastLogin: string;
}

export interface AdminGiftGroup {
  id: string;
  title: string;
  typeId: number;
  typeName: string;
  creatorId: string;
  creatorName: string;
  giftDate: string;
  orderByDate: string;
  recommendedContribution: number | null;
  status: string;
  totalPool: number;
  createdAt: string;
  updatedAt: string;
  gifterCount: number;
  gifteeCount: number;
}

export interface DashboardTodo {
  member_id: string;
  gift_group_id: string;
  gift_group_name: string;
  status: string;
  due_date: string;
  type: string;
  priority: string;
  workflow_state?: string;
  type_name?: string;
  giftees?: string;
}

export interface DashboardCommunication {
  id: string;
  gift_group_id: string;
  type: string;
  message: string;
  read: number;
  created_at: string;
}

export interface DashboardTransaction {
  id: string;
  type: string;
  amount: number;
  description: string;
  created_at: string;
  balance_after: number;
  payment_method?: string;
  payment_provider?: string;
  payment_provider_id?: string;
}

export interface DashboardGiftGroup {
  id: string;
  title: string;
  status: string;
  gift_date: string;
  type_id: string;
  type_name: string;
  role: string;
  member_status: string;
  contributed_amount: number;
  workflow_state?: string;
  giftee_names?: string;
}

export interface DashboardData {
  todos: DashboardTodo[];
  communications: DashboardCommunication[];
  finances: {
    creditBalance: number;
    allocated: number;
    upcoming90Days: number;
    recentTransactions: DashboardTransaction[];
  };
  giftGroups: DashboardGiftGroup[];
}

// Purchase Order types
export interface PurchaseOrder {
  id: string;
  gift_group_id: string;
  gift_suggestion_id: number;
  vendor_name: string;
  product_name: string;
  product_url: string;
  product_price: number;
  shipping_cost: number;
  total_amount: number;
  shipping_name: string | null;
  shipping_phone: string | null;
  shipping_street: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_postal_code: string | null;
  shipping_country: string | null;
  status: 'pending' | 'ordered' | 'shipped' | 'delivered' | 'cancelled' | 'failed';
  vendor_order_id: string | null;
  tracking_number: string | null;
  carrier: string | null;
  requested_at: string;
  ordered_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  order_notes: string | null;
  gift_group_title?: string;
  gift_date?: string;
  ordered_by_name?: string | null;
}

export interface ReadyGift {
  id: number;
  name: string;
  description: string;
  price: number;
  shipping_cost: number;
  url: string;
  image_url: string;
  totalCost: number;
  avg_rank: number | null;
  ranked_by: number;
  vendor_type: string | null;
}

export interface ShippingAddress {
  name: string;
  phone: string | null;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Amazon Product types
export interface AmazonProduct {
  asin: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  imageUrl: string;
  brand?: string;
  category?: string;
  availability?: string;
  url: string;
}

// Admin Gift Suggestion type
export interface AdminGiftSuggestion {
  id: number;
  giftGroupId: string;
  giftGroupTitle: string;
  name: string;
  description: string;
  price: number;
  url: string;
  imageUrl: string;
  source: string;
  purchaseStatus: string;
  purchaseOrderId: string | null;
  suggestionStatus: string;
  createdAt: string;
  suggestedBy: string;
}

export interface VendorAccount {
  id: number;
  vendor_name: string;
  display_name: string;
  account_email: string | null;
  account_notes: string | null;
  is_active: number;
  priority: number;
}

class ApiClient {
  private baseUrl: string;
  private sessionToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Load session token from localStorage on initialization
    if (typeof window !== 'undefined') {
      this.sessionToken = localStorage.getItem('btgtr_session_token');
    }
  }

  setSessionToken(token: string | null) {
    this.sessionToken = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('btgtr_session_token', token);
      } else {
        localStorage.removeItem('btgtr_session_token');
      }
    }
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  isAuthenticated(): boolean {
    return !!this.sessionToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add authorization header if session token exists
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'API request failed');
    }

    return data;
  }

  // Auth endpoints
  async requestMagicLink(
    contact: string,
    context: string = 'dashboard',
    frontendUrl?: string
  ): Promise<MagicLinkRequestResponse> {
    return this.request<MagicLinkRequestResponse>('/auth/request-magic-link', {
      method: 'POST',
      body: JSON.stringify({ contact, context, frontendUrl }),
    });
  }

  async verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse> {
    const response = await this.request<MagicLinkVerifyResponse>('/auth/verify-magic-link', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    
    // Store session token
    if (response.sessionToken) {
      this.setSessionToken(response.sessionToken);
    }
    
    return response;
  }

  async register(contact: string, birthdate: string | null, name: string, memberId?: string): Promise<RegisterResponse> {
    const response = await this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ contact, birthdate, name, memberId }),
    });
    
    // Store session token from registration
    if (response.sessionToken) {
      this.setSessionToken(response.sessionToken);
    }
    
    return response;
  }

  async logout(): Promise<void> {
    this.setSessionToken(null);
  }

  // User endpoints
  async getProfile(): Promise<UserProfile> {
    return this.request<UserProfile>('/users/me');
  }

  async getCurrentUser(): Promise<UserProfile> {
    return this.getProfile();
  }

  async getDashboard(): Promise<DashboardData> {
    return this.request<DashboardData>('/users/me/dashboard');
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    return this.request<UserProfile>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Gift Group endpoints
  async createGiftGroup(data: {
    title: string;
    typeId: number;
    giftDate: string;
    giftTime?: string;
    orderByDate: string;
    recommendedContribution?: number;
    giftees: string[];
    gifters: string[];
  }): Promise<{
    success: boolean;
    giftGroup: {
      id: string;
      title: string;
      status: string;
      invitationsSent: number;
    };
  }> {
    // Include frontendUrl so invitation emails use the creator's current domain
    const requestBody = {
      ...data,
      frontendUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
    };
    return this.request('/gift-groups', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  async getGiftGroups(status: string = 'all'): Promise<{
    giftGroups: DashboardGiftGroup[];
  }> {
    const query = status !== 'all' ? `?status=${status}` : '';
    return this.request(`/gift-groups${query}`);
  }

  async getGiftGroup(id: string): Promise<any> {
    return this.request(`/gift-groups/${id}`);
  }

  async getGiftGroupInvitation(memberId: string): Promise<any> {
    return this.request(`/gift-groups/invitations/${memberId}`);
  }

  async getGiftGroupContributionDetails(giftGroupId: string): Promise<any> {
    return this.request(`/gift-groups/${giftGroupId}/contribution-details`);
  }

  async acceptGiftGroupInvitation(memberId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/invitations/${memberId}/accept`, {
      method: 'POST',
    });
  }

  async declineGiftGroupInvitation(memberId: string, reason?: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/invitations/${memberId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Payment endpoints
  async createPayPalOrder(amount: number): Promise<{
    success: boolean;
    orderId: string;
    approvalUrl: string;
    amount: number;
    currency: string;
  }> {
    return this.request('/payments/add-funds/create-order', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async capturePayPalPayment(orderId: string): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    newBalance: number;
    transactionId: string;
    captureId: string;
  }> {
    return this.request('/payments/add-funds/capture-payment', {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    });
  }

  // Gift Group Contribution
  async contributeToGiftGroup(giftGroupId: string, amount: number): Promise<{
    success: boolean;
    contributedAmount: number;
    newCreditBalance: number;
    memberStatus: string;
    transactionId: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/contribute`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Temporary: Add test funds
  async addTestFunds(amount: number): Promise<{ success: boolean; newBalance: number }> {
    return this.request('/users/me/add-test-funds', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Gift Suggestions endpoints
  async markAIViewed(groupId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/mark-ai-viewed`, {
      method: 'POST',
    });
  }

  async markManualViewed(groupId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/mark-manual-viewed`, {
      method: 'POST',
    });
  }

  async getAISuggestions(
    groupId: string, 
    descriptors: string[], 
    budget?: number,
    prompt?: string
  ): Promise<{ 
    suggestions: any[]; 
    isUsingFallback?: boolean; 
    message?: string;
    promptUsed?: string;
    tokensUsed?: number;
    webSearchUsed?: boolean;
  }> {
    return this.request(`/gift-groups/${groupId}/ai-suggestions`, {
      method: 'POST',
      body: JSON.stringify({ descriptors, budget, prompt }),
    });
  }

  async addGiftSuggestion(groupId: string, data: {
    url: string;
    name?: string;
    description?: string;
    price?: number;
    imageUrl?: string;
    source?: string;
    needsOrderAssistance?: boolean;
    suggestedOrderDate?: string;
    estimatedDeliveryDays?: number;
    shippingCost?: number;
    gifteeId?: number;
  }): Promise<{ success: boolean; suggestion: any; needsEditing?: boolean; error?: string; vendorInfo?: any }> {
    return this.request(`/gift-groups/${groupId}/suggestions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGiftSuggestions(groupId: string): Promise<{ suggestions: any[] }> {
    return this.request(`/gift-groups/${groupId}/suggestions`);
  }

  async updateGiftSuggestion(groupId: string, suggestionId: string, data: {
    name?: string;
    description?: string;
    price?: number;
    url?: string;
  }): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/suggestions/${suggestionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteGiftSuggestion(groupId: string, suggestionId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/suggestions/${suggestionId}`, {
      method: 'DELETE',
    });
  }

  async updateRankings(groupId: string, rankings: Array<{ suggestionId: number; rank: number }>): Promise<{
    success: boolean;
    updatedGroupRankings: Array<{ suggestionId: number; groupRank: number; averageRank: number; rankedBy: number }>;
  }> {
    return this.request(`/gift-groups/${groupId}/rankings`, {
      method: 'PUT',
      body: JSON.stringify({ rankings }),
    });
  }

  async addComment(groupId: string, content: string, suggestionId?: number, mentions?: number[]): Promise<{
    success: boolean;
    comment: any;
  }> {
    return this.request(`/gift-groups/${groupId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ suggestionId, content, mentions }),
    });
  }

  async getComments(groupId: string, suggestionId?: string): Promise<{ comments: any[] }> {
    const query = suggestionId ? `?suggestionId=${suggestionId}` : '';
    return this.request(`/gift-groups/${groupId}/comments${query}`);
  }

  async getGiftGroupActivities(groupId: string): Promise<{ activities: any[] }> {
    return this.request(`/gift-groups/${groupId}/activities`);
  }

  async addGiftGroupMember(giftGroupId: string, contact: string, role: string = 'gifter'): Promise<{
    success: boolean;
    memberId: string;
    contact: string;
    role: string;
    message: string;
  }> {
    // Include frontendUrl so invitation emails use the inviter's current domain
    const requestBody = {
      contact,
      role,
      frontendUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
    };
    return this.request(`/gift-groups/${giftGroupId}/add-member`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  async removeGiftGroupMember(giftGroupId: string, memberId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async resendGiftGroupInvitation(giftGroupId: string, memberId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    // Include frontendUrl so invitation emails use the inviter's current domain
    const requestBody = {
      frontendUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
    };
    return this.request(`/gift-groups/${giftGroupId}/members/${memberId}/resend-invite`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  async getShippingAddressInfo(giftGroupId: string): Promise<{
    giftGroupId: string;
    giftGroupTitle: string;
    typeId: number;
    typeName: string;
    collectingForSelf: boolean;
    membership: {
      id: string;
      role: string;
      workflowState: string;
      hasAddress: boolean;
      address: {
        name: string | null;
        phone: string | null;
        street: string | null;
        city: string | null;
        state: string | null;
        postalCode: string | null;
        country: string | null;
      };
      enteredBy: string | null;
      enteredByName: string | null;
    };
    giftees: Array<{
      id: string;
      name: string;
      contact: string;
      hasAddress: boolean;
      address: {
        name: string | null;
        phone: string | null;
        street: string | null;
        city: string | null;
        state: string | null;
        postalCode: string | null;
        country: string | null;
      };
      enteredBy: string | null;
      enteredByName: string | null;
    }>;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/shipping-address`);
  }

  async updateShippingAddress(
    giftGroupId: string,
    data: {
      targetMemberId?: string;
      address?: {
        name: string;
        phone: string;
        street: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
      skip: boolean;
    }
  ): Promise<{
    success: boolean;
    workflowState: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/shipping-address`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getMemberShippingAddress(giftGroupId: string, memberId: string): Promise<{
    memberId: string;
    name: string;
    contact: string;
    role: string;
    hasAddress: boolean;
    address: {
      name: string | null;
      phone: string | null;
      street: string | null;
      city: string | null;
      state: string | null;
      postalCode: string | null;
      country: string | null;
    };
    updatedAt: string | null;
    enteredBy: string | null;
    enteredByName: string | null;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/members/${memberId}/shipping-address`);
  }

  async confirmShippingAddress(
    giftGroupId: string,
    targetMemberId: string
  ): Promise<{
    success: boolean;
    confirmedBy: string[];
    workflowState: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/shipping-address/confirm`, {
      method: 'POST',
      body: JSON.stringify({ targetMemberId }),
    });
  }

  // Admin endpoints
  async getAdminStats(): Promise<{
    totalUsers: number;
    totalGiftGroups: number;
    totalEscrow: number;
    purchasesCount: number;
    purchasesAmount: number;
  }> {
    return this.request('/admin/stats');
  }

  async getAdminUsers(options?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<{
    users: UserProfile[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/users${query}`);
  }

  async getAdminGiftGroups(options?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: string;
  }): Promise<{
    giftGroups: AdminGiftGroup[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.status) params.append('status', options.status);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/gift-groups${query}`);
  }

  async getAdminGiftSuggestions(options?: {
    limit?: number;
    offset?: number;
    search?: string;
    vendor?: string;
  }): Promise<{
    suggestions: AdminGiftSuggestion[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.vendor) params.append('vendor', options.vendor);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/gift-suggestions${query}`);
  }

  // Amazon SP-API endpoints
  async amazonProductLookup(asin: string): Promise<AmazonProduct> {
    return this.request(`/amazon/product/${asin}`);
  }

  async amazonExtractAsin(url: string): Promise<{
    asin: string;
    url: string;
  }> {
    const params = new URLSearchParams({ url });
    return this.request(`/amazon/extract-asin?${params.toString()}`);
  }

  async amazonSearchProducts(keywords: string, limit?: number): Promise<{
    results: AmazonProduct[];
    count: number;
    keywords: string;
    environment: string;
  }> {
    return this.request('/amazon/search', {
      method: 'POST',
      body: JSON.stringify({ keywords, limit: limit || 10 }),
    });
  }

  async amazonHealthCheck(): Promise<{
    status: string;
    environment: string;
    credentials: Record<string, boolean>;
    missing: string[];
    ready: boolean;
  }> {
    return this.request('/amazon/health');
  }

  // Purchase Orders endpoints
  async getPendingPurchases(): Promise<{
    success: boolean;
    purchases: PurchaseOrder[];
    count: number;
  }> {
    return this.request('/purchases/pending');
  }

  async getReadyPurchases(giftGroupId: string): Promise<{
    success: boolean;
    giftGroup: {
      id: string;
      title: string;
      giftDate: string;
      orderByDate: string;
      totalPool: number;
      remainingPool: number;
    };
    shippingAddress: ShippingAddress | null;
    readyToPurchase: ReadyGift[];
    count: number;
  }> {
    return this.request(`/purchases/gift-group/${giftGroupId}/ready`);
  }

  async createPurchaseOrder(giftGroupId: string, giftSuggestionId: number, shippingAddress?: ShippingAddress): Promise<{
    success: boolean;
    purchaseOrder: {
      id: string;
      vendorName: string;
      productName: string;
      productUrl: string;
      totalAmount: number;
      status: string;
      shippingAddress: ShippingAddress | null;
    };
  }> {
    return this.request('/purchases', {
      method: 'POST',
      body: JSON.stringify({
        giftGroupId,
        giftSuggestionId,
        shippingAddress
      }),
    });
  }

  async updatePurchaseOrder(orderId: string, data: {
    status?: 'pending' | 'ordered' | 'shipped' | 'delivered' | 'cancelled' | 'failed';
    vendorOrderId?: string;
    trackingNumber?: string;
    carrier?: string;
    notes?: string;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/purchases/${orderId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getPurchaseOrder(orderId: string): Promise<{
    success: boolean;
    purchaseOrder: PurchaseOrder;
    history: Array<{
      id: number;
      purchase_order_id: string;
      previous_status: string | null;
      new_status: string;
      changed_by_user_id: string | null;
      notes: string | null;
      created_at: string;
      changed_by_name?: string;
    }>;
  }> {
    return this.request(`/purchases/${orderId}`);
  }

  async getVendorAccounts(): Promise<{
    success: boolean;
    vendors: VendorAccount[];
  }> {
    return this.request('/purchases/vendors');
  }

  // White Elephant game endpoints
  async getWhiteElephantGameState(giftGroupId: string): Promise<{
    giftGroupId: string;
    gameId?: string;
    status: 'not_started' | 'order_generated' | 'in_progress' | 'completed';
    pickOrder: Array<{
      userId: string;
      name: string;
      order: number;
      isCurrent?: boolean;
    }>;
    currentPickerIndex: number | null;
    currentPickerUserId: string | null;
    currentPickerName: string | null;
    isYourTurn: boolean;
    pickTimeRemaining: number | null;
    pickTimeoutSeconds: number;
    isFirstPickerFinalTurn: boolean;
    lastStolenGiftId: number | null;
    unopenedGifts: Array<{
      giftId: number;
      giftName: string;
      giftDescription: string;
      giftPrice: number;
      giftImageUrl: string;
      suggestedByUserId: string;
      suggestedByName: string;
    }>;
    openedGifts: Array<{
      pickId: string;
      giftId: number;
      giftName: string;
      giftDescription: string;
      giftPrice: number;
      giftImageUrl: string;
      currentOwnerUserId: string;
      currentOwnerName: string;
      stealCount: number;
      canBeStolen: boolean;
      suggestedByUserId: string;
      suggestedByName: string;
    }>;
    frozenGifts: Array<{
      pickId: string;
      giftId: number;
      giftName: string;
      giftDescription: string;
      giftPrice: number;
      giftImageUrl: string;
      currentOwnerUserId: string;
      currentOwnerName: string;
      stealCount: number;
      suggestedByUserId: string;
      suggestedByName: string;
    }>;
    canSteal: number[];
    canPick: number[];
    turnHistory: Array<{
      turnNumber: number;
      actionType: 'pick' | 'steal';
      actorUserId: string;
      actorName: string;
      previousOwnerUserId?: string;
      previousOwnerName?: string;
      giftName: string;
      createdAt: string;
    }>;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/game-state`);
  }

  async generateWhiteElephantPickOrder(giftGroupId: string): Promise<{
    success: boolean;
    gameId: string;
    pickOrder: Array<{
      userId: string;
      name: string;
      order: number;
    }>;
    status: string;
    orderGeneratedAt: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/generate-order`, {
      method: 'POST',
    });
  }

  async startWhiteElephantGame(giftGroupId: string): Promise<{
    success: boolean;
    status: string;
    currentPickerIndex: number;
    currentPickerUserId: string;
    gameStartedAt: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/start-game`, {
      method: 'POST',
    });
  }

  async whiteElephantPick(giftGroupId: string, giftId: number): Promise<{
    success: boolean;
    pickId: string;
    giftId: number;
    giftName: string;
    turnNumber: number;
    nextPickerIndex: number;
    nextPickerUserId: string | null;
    isFirstPickerFinalTurn: boolean;
    gameComplete: boolean;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/pick`, {
      method: 'POST',
      body: JSON.stringify({ giftId }),
    });
  }

  async whiteElephantSteal(giftGroupId: string, giftId: number): Promise<{
    success: boolean;
    giftId: number;
    giftName: string;
    previousOwner: string;
    newStealCount: number;
    isFrozen: boolean;
    turnNumber: number;
    nextPickerUserId: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/steal`, {
      method: 'POST',
      body: JSON.stringify({ giftId }),
    });
  }

  async endWhiteElephantGame(giftGroupId: string): Promise<{
    success: boolean;
    status: string;
    gameEndedAt: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/white-elephant/end-game`, {
      method: 'POST',
    });
  }

  // Feedback endpoint
  async submitFeedback(data: {
    feedback: string;
    currentPage: string;
    currentView?: string;
    giftGroupId?: string;
    giftGroupTitle?: string;
    originalFeedbackId?: string;
  }): Promise<{
    success: boolean;
    message: string;
    feedbackId: string;
  }> {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async replyToFeedback(data: {
    toUserId: string;
    feedback: string;
    originalFeedbackId?: string;
  }): Promise<{
    success: boolean;
    message: string;
    feedbackId: string;
  }> {
    return this.request('/feedback/reply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);